import { html, LitElement, nothing, PropertyValues, unsafeCSS } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import { property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { until } from 'lit/directives/until.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import type { TemplateResult } from 'lit';
import type {
  DataTableFilterDescriptor,
  DataTableDataProvider,
} from './data-table-provider';
import {
  DataTableController,
  DataTableControllerState,
  DataTableControllerCriteria,
} from './data-table-controller';

import './data-table-paginator';
import { DataTablePaginator } from './data-table-paginator';
import {
  claimTakeover,
  releaseTakeover,
  type FullscreenTakeoverHolder,
} from '../../utility/fullscreen-takeover';
// Styles live in a real .css file so CSS tooling (language server, CSS-aware
// prettier, stylelint's raw-colour guard) can see them. `?inline` hands us the
// text instead of injecting a <style> into the document, which would never
// reach this component's shadow root; unsafeCSS then wraps it as the same
// CSSResult a `css` template produces, so Lit still adopts one shared
// constructable stylesheet per class.
import dataTableStyles from './data-table.styles.css?inline';

export function awaitableTemplate(
  template:
    | TemplateResult
    | string
    | Promise<TemplateResult>
    | Promise<string>
    | HTMLElement
    | Promise<HTMLElement>
    | undefined
    | null
    | (() => string | TemplateResult)
    | (() => TemplateResult[])
    | (() => Promise<string | TemplateResult>)
    | (() => Promise<TemplateResult[]>)
) {
  if (!template) return html``;
  const templateValue = typeof template === 'function' ? template() : template;

  return templateValue instanceof Promise
    ? until(templateValue)
    : templateValue;
}

/**
 * Returns True if handled. if false, the event will go to the cellclick
 */
export type DataTableCellClickEvent = (
  e: Event,
  table: DataTable,
  item: unknown,
  fieldName: string
) => boolean;

// Id is a unique value to query on if set.
export interface DataTableColumnDef {
  id?: string;
  title: string | TemplateResult | (() => string | TemplateResult);
  fieldName: string;
  classes?: string;
  width?: string; // CSS width value e.g. '150px', '10rem', 'auto'
  minWidth?: string; // CSS min-width value
  maxWidth?: string; // CSS max-width value
  fixed?: 'left' | 'right';
  sortable?: boolean;
  isDefaultSortDirectionAscending?: boolean;
  displayValue?: (
    table: DataTable,
    item: any, // eslint-disable-line @typescript-eslint/no-explicit-any
    index: number,
    fieldName: string
  ) => TemplateResult | string;
  /**
   * Single-line the cell with an ellipsis when its content does not fit, expanding under the
   * pointer (the overflow affordance, architect 2026-09-05). Defaults ON for plain field
   * columns - they are text, and a wrapped name inflates the spreadsheet-tight rows - and OFF
   * for displayValue columns, whose badges and chip stacks must never be forced onto one
   * line. A text-rendering displayValue column opts in with true; a plain column that must
   * wrap opts out with false.
   */
  clip?: boolean;
  click?: DataTableCellClickEvent;
  visible?: boolean;
  columnAggregate?: () => TemplateResult | string;
  align?: 'left' | 'center' | 'right';
}

export type DataTableRowExtensionTemplateEvent =
  | ((item: unknown) => TemplateResult)
  | ((item: unknown) => Promise<TemplateResult>)
  | ((item: unknown) => DataTableExtensionResult)
  | ((item: unknown) => Promise<DataTableExtensionResult>);

export type DataTableRowExtensionTemplate =
  | TemplateResult
  | Promise<TemplateResult>
  | DataTableExtensionResult
  | Promise<DataTableExtensionResult>;

export type DataTableExtensionTemplateProvider =
  DataTableRowExtensionTemplate | DataTableRowExtensionTemplateEvent;

/** Compound result for extension templates */
export interface DataTableExtensionResult {
  template: TemplateResult;
}

interface DataTableExtension {
  itemKey: unknown;
  extensionProvider: DataTableExtensionTemplateProvider;
}

/** Template function for group header rows */
export type DataTableGroupHeaderTemplate = (
  groupValue: unknown,
  groupItems: object[],
  table: DataTable
) => TemplateResult;

export interface TableItemMove {
  sourceIndex: number;
  targetIndex: number;
}

export interface DataTableFetchOptions {
  sliceStart?: number;
  requiresCount?: boolean;
  append?: boolean;
}

/**
 * @event fetchtemplate - Emitted requesting row template.
 * @event {{ table: DataTable, item: Object }} rowclick - Emitted when a row is clicked.
 * @event {{ table: DataTable, selectedkeys: Object }} rowselect - Emitted when a row is selected.
 * @event {{ table: DataTable, item: Object, colDef: DataTableColumnDef, originalEvent: Event }} cellclick - Emitted when a cell is clicked.
 * @event {{ Object : TableItemMove }} -table-item-move - Emitted when items are moved (drag/drop).
 *
 * @tag agwa-lit-table
 */
export class DataTable extends LitElement implements FullscreenTakeoverHolder {
  static styles = [unsafeCSS(dataTableStyles)];

  private dragSrcEl: HTMLDivElement | null = null;
  private lastDropTarget: HTMLElement | null = null;
  private draggedIndex: number | null = null;
  private noDropClass = 'no-drop-allowed';
  private controller = new DataTableController<object>();
  private controllerUnsubscribe?: () => void;

  @property() rowClass = 'data-table-row';
  @property() colClass = 'data-table-cell';

  @property({ type: String })
  enableDrag?: string;
  @property({ attribute: false })
  dataProvider?: DataTableDataProvider<object>;
  @property({ attribute: false })
  dataFilters: DataTableFilterDescriptor[] = [];
  @property({ type: String })
  freeTextFilter?: string;
  @property({ type: Boolean })
  autoFetchOnLoad = true;

  @property({ type: String })
  bodyMaxHeight?: string;

  @property({ type: String })
  minTableWidth?: string;

  /**
   * When true, columns without explicit width will be auto-sized based on
   * header and content. Set to false to use equal flex distribution.
   */
  @property({ type: Boolean })
  autoSizeColumns = true;

  @property({ type: Boolean })
  clickrows = false;
  @property({ type: String })
  selectmode = 'none'; // none | single | multi
  @property({ type: String })
  tablestyle = 'standardtable'; //standard, nested, "", small
  @property({ type: Boolean })
  requiresCount = false;
  @property({ type: Number })
  rowCount = 0;
  @property({ type: Boolean })
  showLoadMore = false;

  /**
   * NEVER RENDER THE PAGINATOR, whatever the row count.
   *
   * For a table that lives inside a panel or a section beside other content, where a pager bar is
   * not a thing the surrounding design has room for. It is a statement about the CONTAINER, so it
   * outranks the row count rather than being weighed against it.
   *
   * It exists because seven call sites were already suppressing the pager by accident: none of
   * them sets `pageLength`, so the 3000 default meant `pageCount` was 1 and the pager never
   * appeared. That held only while those lists stayed small, and nothing in their markup said so.
   * Stating it here closes a cliff whose edge nobody could see (architect, 2026-09-19).
   *
   * Note what else disappears with it: the fullscreen toggle rides on the paginator row, so a
   * table that hides the pager and does not use load-more offers no fullscreen control. That is
   * already true of every table whose rows fit one page, which is nearly all of them.
   */
  @property({ type: Boolean })
  hidePagination = false;
  /**
   * HOW THIS TABLE KNOWS ONE ROW FROM ANOTHER. Supply a function returning the row's key and the
   * table holds that value, not the object it came from.
   *
   * IT IS NOT OPTIONAL FOR A LIST THAT EXPANDS ANYTHING. Without it `getPrimaryKey` falls back to
   * the row OBJECT, and object identity survives only until the next fetch: every provider builds
   * fresh objects, so a filter keystroke, a save or an SSE nudge replaces them all and every open
   * row shuts itself with nothing on screen saying why. `expansionKey` warns the first time a row
   * is expanded without one, because that fallback is invisible in every other way.
   *
   * A COMPOUND KEY IS COMPOSED HERE, by the caller, because only the caller knows which fields
   * identify its rows and which separator cannot appear inside their values:
   *
   * ```ts
   * .keyevent=${(item: SiteRow) => item.siteId}
   * .keyevent=${(item: LinkRow) => `${item.ownerId};${item.siteId}`}
   * ```
   *
   * Declare it as a class field rather than an inline arrow where the host re-renders often: a new
   * function identity on each render is a changed reactive property, which costs the table an extra
   * render pass it has no use for.
   *
   * The key must be a value `===` compares usefully (a string or a number). Returning an object
   * puts the fallback back exactly as it was, silently.
   *
   * There is no attribute form. One was written across 21 call sites as `primaryKey="id"`, read
   * like the mechanism, and did nothing at all: LitElement maps an attribute only onto a declared
   * reactive property, and none was declared. It was deleted rather than implemented (architect,
   * 2026-09-19) because a function references real fields, which the compiler checks, where a field
   * name in a string is a runtime guess this component would have had to validate and recover from.
   */
  @property({ attribute: false })
  keyevent?: (item: any) => unknown; // eslint-disable-line @typescript-eslint/no-explicit-any

  @property({ attribute: false })
  rowidevent?: (item: any) => string | null; // eslint-disable-line @typescript-eslint/no-explicit-any

  @property({ attribute: false })
  itemRowClassEvent?: (item: any) => string; // eslint-disable-line @typescript-eslint/no-explicit-any
  @property({ attribute: false })
  checkEnabledDragEvent?: (item: any) => boolean; // eslint-disable-line @typescript-eslint/no-explicit-any

  /** Field name to group rows by. Data should be sorted by this field. */
  @property({ type: String })
  groupBy?: string;

  /**
   * Function to extract a group key from a row item.
   * When provided, this takes precedence over the `groupBy` field name.
   * The returned string is used as the group key and for ordering groups.
   */
  @property({ attribute: false })
  getGroupByKey?: (item: object) => string | null;

  /** Custom template for group header rows. If not provided, uses default. */
  @property({ attribute: false })
  groupHeaderTemplate?: DataTableGroupHeaderTemplate;

  private _columns: DataTableColumnDef[] = [
    {
      title: 'Column1',
      fieldName: '',
      classes: 'col-6',
      displayValue: () => 'One',
    },
    {
      title: 'Column2',
      fieldName: '',
      classes: 'col-6',
      displayValue: () => 'Two',
    },
  ];

  @property({ type: Array })
  set columns(value: DataTableColumnDef[]) {
    const nextColumns = value ?? [];
    this.validateFixedColumns(nextColumns);
    const oldValue = this._columns;
    this._columns = nextColumns;
    this.fixedColumnLayoutDirty = true;
    this.requestUpdate('columns', oldValue);
  }

  get columns() {
    return this.enableDrag === 'true'
      ? [
          {
            title: '',
            classes: 'colpxmax-30 drag-grab-handle',
            fieldName: 'xx',
            displayValue: (
              _table: DataTable,
              _item: unknown,
              _index: number
            ) => {
              return html`<span
                @touchstart="${this.dragtouchstart}"
                @touchmove="${this.dragtouchmove}"
                @touchend="${this.dragtouchend}"
                @touchcancel="${this.handleTouchCancel}"
                class="draggable-item"
                ><i class="fa-solid fa-grip-vertical"></i
              ></span>`;
            },
          },
          ...this._columns,
        ]
      : this._columns;
  }

  private validateFixedColumns(columns: DataTableColumnDef[]) {
    type Phase = 'left' | 'middle' | 'right';
    let phase: Phase = 'left';
    columns.forEach((column) => {
      const fixed = column.fixed;
      if (fixed === 'left') {
        if (phase !== 'left') {
          throw new Error(
            'Fixed left columns must be defined contiguously at the start of the column set.'
          );
        }
        return;
      }

      if (fixed === 'right') {
        phase = 'right';
        return;
      }

      if (phase === 'right') {
        throw new Error(
          'Non-fixed columns cannot appear after fixed right columns. Move fixed right columns to the end.'
        );
      }

      phase = 'middle';
    });
  }

  @state()
  data: object[] = [];
  @state()
  private controllerState: DataTableControllerState<object> | null = null;
  @state()
  private isFullscreen = false;
  @state()
  private _computedWidths: Map<string, number> = new Map();
  private _autoSizeScheduled = false;
  private hasRequestedData = false;
  private suppressNextProviderReload = false;
  @property({ type: Number })
  pageLength = 3000;
  @state()
  private _extensions: DataTableExtension[] = [];

  /**
   * The body this table last rendered, shown while the next one is built (#398 C9). bodyTemplate
   * is async, so the body goes through until(); with an empty placeholder, every render of the
   * table - a new provider, new columns, a selection, a resize - removed every row and built them
   * all again a microtask later, destroying whatever a cell held (an upload in flight, an open
   * panel, focus). With the last body as the placeholder the rows stay, and the new body is diffed
   * onto them by row key (getPrimaryKey), so a row still there keeps its elements.
   */
  private lastBody: TemplateResult | TemplateResult[] = html``;

  /**
   * Each open extension panel's last rendered content, by row key, shown while its next content
   * is built, for the same reason as lastBody: the panel's provider is async too, and the
   * "Loading" placeholder replaced the panel on every render. Dropped when the panel is closed or
   * replaced (addExtension, remExtension).
   */
  private lastExtensionContent = new Map<unknown, TemplateResult>();

  /**
   * Supplies the CHILD ROWS of an item, or nothing when it has none.
   *
   * A child row is a row of THIS table, rendered through the same column definitions and the same
   * cell pipeline as its parent, so its values sit under the same headings by construction. That is
   * the whole point of the feature and the reason it lives here rather than in a consumer: a
   * consumer rendering its own nested table has to RE-STATE this table's column widths and pinned
   * segments, and the copy drifts the moment either side changes. On the company list that drift had
   * already happened - the nested sites table said "Inactive" where the company row beside it said
   * "Blocked".
   *
   * Child items are ordinary items. The table does not interpret them; it renders them through the
   * columns it was given, so a column's `fieldName` or `displayValue` sees the child exactly as it
   * sees a parent. A consumer that wants a column blank on one and filled on the other says so in
   * the data it supplies, not in a second set of columns.
   */
  @property({ attribute: false })
  childRowsProvider?: (item: object) => object[] | undefined;

  /**
   * Which items are currently showing their child rows.
   *
   * Deliberately SEPARATE from `_extensions`, rather than reusing it. The two are independent
   * things a row can be doing - a row may show child rows, an extension panel, both, or neither -
   * and folding them together would make "expanded" ambiguous the first time a consumer wanted one
   * without the other. Keyed by primary key rather than by item reference so it survives a refetch
   * that produces new object identities, which is what makes filter-driven auto-expansion possible
   * at all.
   */
  @state()
  private _childExpandedKeys: unknown[] = [];
  private _firstConnected = false;
  private fixedColumnLayoutDirty = true;
  private fixedColumnOffsetFrame: number | null = null;
  /**
   * Whether the table is wider than its scroller, measured from the rows' own content width. A
   * right-pinned column is pinned (sticky at the edge, with its shadow) only while this holds;
   * when every column fits it follows the last column as an ordinary one, so a table narrower
   * than its container shows no empty band between its columns and its actions
   * (settings-lookup-tables-acceptance.md rule 1). The rows keep `min-width: max-content` in both
   * states, so the measurement does not change with the state it drives and cannot oscillate.
   */
  @state()
  private overflowsSideways = false;
  private overflowMeasureFrame: number | null = null;
  /**
   * The column indices that currently carry an inline pinning offset, per side.
   *
   * It is the only record of a cell that USED to be pinned: `data-fixed` is gone from that cell
   * by the time anything notices, while the inline `left` or `right` remains and shifts it. See
   * `applyOffsetsForSide`.
   */
  private offsetColumnsWritten: Record<'left' | 'right', Set<number>> = {
    left: new Set(),
    right: new Set(),
  };
  private resizeObserver: ResizeObserver | null = null;
  private bodyScrollElement: HTMLElement | null = null;
  private headerContainerElement: HTMLElement | null = null;
  private bodyScrollListener?: (event: Event) => void;

  @property({ type: String })
  sortField?: string;
  @property({ type: Boolean })
  sortAscending?: boolean;
  @property({ type: Array })
  selectedKeys: unknown[] = [];

  @property({ type: Number })
  private _pageIndex = 0;

  constructor() {
    super();
    this.controllerUnsubscribe = this.controller.subscribe((state) => {
      this.controllerState = state;
      this.data = [...state.items];
      this.rowCount =
        state.totalCount !== undefined ? state.totalCount : this.data.length;
    });
  }

  public get pageIndex(): number {
    return this._pageIndex;
  }

  public set pageIndex(value: number) {
    this._pageIndex = this.pageNumberCheck(value);
    if (!this.dataProvider) return;
    if (!this.autoFetchOnLoad && !this.hasRequestedData) return;
    void this.requestDataFromProvider({
      reset: true,
      sliceStart: this._pageIndex * this.pageLength,
    });
  }

  private get shouldUseLoadMore(): boolean {
    return !!this.dataProvider && this.showLoadMore;
  }

  protected get standardtable(): boolean {
    return this.tablestyle === 'standardtable' || this.tablestyle === '';
  }

  protected get nestedtable(): boolean {
    return this.tablestyle === 'nestedtable';
  }

  protected get halftable(): boolean {
    return this.tablestyle === 'halftable';
  }

  protected get isSortingAscending(): boolean {
    return this.sortAscending === undefined || this.sortAscending;
  }

  protected get pageCount(): number {
    return Math.ceil(this.rowCount / this.pageLength);
  }

  protected shouldRenderPaginator(): boolean {
    // The host's refusal is checked FIRST and unconditionally: it is a statement about where this
    // table lives, which no row count can overrule.
    if (this.hidePagination) return false;
    if (this.shouldUseLoadMore) return false;
    return this.pageCount > 1;
  }

  protected get isSelectable(): boolean {
    return this.selectmode !== 'none';
  }

  connectedCallback(): void {
    if (!this._firstConnected) this.configure();
    this._firstConnected = true;
    this.observeSizeChanges();
    super.connectedCallback();
  }

  disconnectedCallback(): void {
    // A route change, or a dialog closing on its own Escape, removes the host while the table is
    // still full screened - reachable today from the two site-assignment dialogs. The document
    // lock and the key listener are global state and would otherwise outlive the table, and the
    // table's OWN state goes with them: a host that MOVED this element rather than destroying it
    // would otherwise get it back drawn over the whole viewport while the registry believed
    // nobody held the takeover.
    releaseTakeover(this);
    this.applyFullscreen(false);
    this.controllerUnsubscribe?.();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.fixedColumnOffsetFrame !== null) {
      cancelAnimationFrame(this.fixedColumnOffsetFrame);
      this.fixedColumnOffsetFrame = null;
    }
    if (this.overflowMeasureFrame !== null) {
      cancelAnimationFrame(this.overflowMeasureFrame);
      this.overflowMeasureFrame = null;
    }
    this.teardownScrollContainers();
    super.disconnectedCallback();
  }

  protected updated(changedProps: PropertyValues): void {
    super.updated(changedProps);

    let shouldReload = false;
    const canAutoLoad = this.autoFetchOnLoad || this.hasRequestedData;

    if (changedProps.has('dataProvider')) {
      if (this.dataProvider) {
        this.controller.setSliceLength(this.pageLength);
        this.controller.setProvider(this.dataProvider);
        if (this.suppressNextProviderReload)
          this.suppressNextProviderReload = false;
        else if (canAutoLoad) shouldReload = true;
      } else {
        this.hasRequestedData = false;
      }
    }

    if (
      changedProps.has('dataFilters') ||
      changedProps.has('freeTextFilter') ||
      changedProps.has('requiresCount')
    ) {
      if (this.hasRequestedData) shouldReload = true;
    }

    if (changedProps.has('pageLength')) {
      this.controller.setSliceLength(this.pageLength);
      if (this.dataProvider && this.hasRequestedData) shouldReload = true;
    }

    if (shouldReload && this.dataProvider && canAutoLoad) {
      void this.requestDataFromProvider({ reset: true });
    }

    if (
      this.fixedColumnLayoutDirty ||
      changedProps.has('columns') ||
      changedProps.has('data') ||
      changedProps.has('enableDrag') ||
      changedProps.has('sortField') ||
      changedProps.has('sortAscending')
    ) {
      this.queueFixedColumnOffsets();
      this.fixedColumnLayoutDirty = false;
    }

    // Every render can change the table's width without a data or column change (auto-sized
    // columns land two frames after the data, and end in a bare requestUpdate), and the scroller's
    // own box does not change when only its content grows, so no ResizeObserver sees it. Measured
    // after each render, so a table that grew past its window is pinned (rule 1).
    this.queueOverflowMeasure();

    // Schedule auto-sizing when data or columns change
    if (changedProps.has('data') || changedProps.has('columns')) {
      this.scheduleAutoSize();
    }

    // Re-trigger auto-sizing when autoSizeColumns is toggled on
    if (changedProps.has('autoSizeColumns')) {
      if (this.autoSizeColumns) {
        // Clear old widths and re-measure
        this._computedWidths = new Map();
        this.scheduleAutoSize();
      } else {
        // Clear computed widths when auto-sizing is disabled
        this._computedWidths = new Map();
      }
    }

    this.setupScrollContainers();
  }

  configure() {
    //override;
  }

  private composeCriteria(): DataTableControllerCriteria {
    const sort: DataTableControllerCriteria['sort'] = this.sortField
      ? [
          {
            field: this.sortField,
            direction: this.isSortingAscending ? 'asc' : 'desc',
          },
        ]
      : [];
    return {
      sort: sort,
      filters: this.dataFilters,
      freeText: this.freeTextFilter,
      requiresCount: this.requiresCount,
    };
  }

  private async requestDataFromProvider(options?: {
    reset?: boolean;
    sliceStart?: number;
    requiresCount?: boolean;
  }) {
    if (!this.dataProvider) return;
    this.controller.setProvider(this.dataProvider);

    if (options?.reset === false) {
      await this.controller.loadMore({
        sliceStart: options.sliceStart,
        sliceLength: this.pageLength,
        requiresCount: options.requiresCount,
      });
    } else {
      await this.controller.loadInitial(this.composeCriteria(), {
        sliceStart: options?.sliceStart ?? 0,
        sliceLength: this.pageLength,
        requiresCount: options?.requiresCount ?? this.requiresCount,
      });
    }

    this.hasRequestedData = true;
  }

  public async fetchData(options?: DataTableFetchOptions) {
    await this.requestDataFromProvider({
      reset: options?.append !== true,
      sliceStart: options?.sliceStart,
      requiresCount: options?.requiresCount,
    });
  }

  isExpanded(item: object) {
    const itemKey = this.getPrimaryKey(item);
    return this._extensions.find((x) => x.itemKey === itemKey);
  }

  /** The child rows this item would show, empty when it has none or no provider is set. */
  childRowsFor(item: object): object[] {
    return this.childRowsProvider?.(item) ?? [];
  }

  /** Whether this item has anything to expand INTO, so a caller can decide to offer the affordance. */
  hasChildRows(item: object): boolean {
    return this.childRowsFor(item).length > 0;
  }

  isChildRowsExpanded(item: object): boolean {
    return this._childExpandedKeys.includes(this.getPrimaryKey(item));
  }

  expandChildRows(item: object) {
    // Through `expansionKey`, which is what reports a table opening rows it cannot identify.
    const itemKey = this.expansionKey(item);
    if (this.isChildRowsExpanded(item)) return;
    this._childExpandedKeys = [...this._childExpandedKeys, itemKey];
  }

  collapseChildRows(item: object) {
    const itemKey = this.getPrimaryKey(item);
    this._childExpandedKeys = this._childExpandedKeys.filter(
      (key) => key !== itemKey
    );
  }

  toggleChildRows(item: object) {
    if (this.isChildRowsExpanded(item)) this.collapseChildRows(item);
    else this.expandChildRows(item);
  }

  /**
   * Replace the whole expanded set in one go.
   *
   * Exists for the filter case: when a search or filter matches a row only because one of its
   * CHILDREN matched, the match is invisible until the row is opened, and making a person open rows
   * to find out why they were returned is not a search result. A consumer recomputes the set after
   * each fetch and states it here, rather than adding and removing one at a time against whatever
   * the previous filter happened to leave behind.
   */
  setExpandedChildRows(items: object[]) {
    // An EMPTY set is a consumer closing everything, which needs no key and reports no problem;
    // only a set that actually opens something goes through the guard.
    const next = items.map((item) => this.expansionKey(item));
    // Assigning unconditionally would re-render on every fetch even when nothing changed, and the
    // table restores scroll position on render, so a no-op assignment is visible to a user as a jump.
    const unchanged =
      next.length === this._childExpandedKeys.length &&
      next.every((key) => this._childExpandedKeys.includes(key));
    if (unchanged) return;
    this._childExpandedKeys = next;
  }

  addExtension(item: object, template: DataTableExtensionTemplateProvider) {
    // Through `expansionKey`: registering a panel is the moment a row's identity has to outlive
    // the objects on screen, so it is the moment a missing key is worth saying out loud.
    const itemKey = this.expansionKey(item);
    // Save scroll position before state change triggers re-render
    const scrollContainer = this.bodyScrollElement;
    const scrollTop = scrollContainer?.scrollTop ?? 0;
    const scrollLeft = scrollContainer?.scrollLeft ?? 0;

    this._extensions = [
      ...this._extensions.filter((x) => x.itemKey !== itemKey),
      { itemKey: itemKey, extensionProvider: template },
    ];
    // A new panel for the row: what an earlier one rendered is not its content.
    this.lastExtensionContent.delete(itemKey);

    // Restore scroll position after render
    void this.updateComplete.then(() => {
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollTop;
        scrollContainer.scrollLeft = scrollLeft;
      }
    });
  }

  remExtension(item: object) {
    const itemKey = this.getPrimaryKey(item);
    // Save scroll position before state change triggers re-render
    const scrollContainer = this.bodyScrollElement;
    const scrollTop = scrollContainer?.scrollTop ?? 0;
    const scrollLeft = scrollContainer?.scrollLeft ?? 0;

    this._extensions = this._extensions.filter((x) => x.itemKey !== itemKey);
    this.lastExtensionContent.delete(itemKey);

    // Restore scroll position after render
    void this.updateComplete.then(() => {
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollTop;
        scrollContainer.scrollLeft = scrollLeft;
      }
    });
  }

  fetchTemplateEvent(item: object) {
    this.dispatchCustom('fetchtemplate', { table: this, item: item });
  }

  afterRowTemplate(item: object, index: number) {
    const event = {
      table: this,
      item: item,
      index: index,
      template: undefined,
    };
    this.dispatchCustom('after-row-render', event);
    return event.template;
  }

  beforeRowTemplate(item: object, index: number) {
    const event = {
      table: this,
      item: item,
      index: index,
      template: undefined,
    };
    this.dispatchCustom('before-row-render', event);
    return event.template;
  }

  rowClickEvent(item: object) {
    this.dispatchCustom('rowclick', { table: this, item: item });
  }

  rowSelectEvent() {
    this.dispatchCustom('rowselect', {
      table: this,
      selectedkeys: this.selectedKeys,
    });
  }

  cellClickEvent(
    originalEvent: Event,
    item: object,
    colDef: DataTableColumnDef
  ) {
    this.dispatchCustom('cellclick', {
      table: this,
      item: item,
      colDef: colDef,
      originalEvent: originalEvent,
    });
  }

  getPrimaryKey(item: object): unknown {
    if (this.keyevent) return this.keyevent(item);
    return item;
  }

  /** One warning per table instance: a per-row, per-render warning is noise nobody reads. */
  private hasWarnedAboutRowIdentity = false;

  /**
   * The key for a row that is being EXPANDED, and the one place that says so out loud when there
   * is no real key to use.
   *
   * Expansion is the one feature that cannot survive the object-identity fallback: a key is
   * compared against rows the table will hold LATER, and by then every object has been replaced by
   * the next fetch. Selection and the render key are compared within one render and do not have
   * that problem, which is why this guard sits here and not inside `getPrimaryKey`.
   *
   * It warns from the ACTIONS that open a row rather than from the render path. A list that never
   * expands anything has no problem to report, and most lists in this product never do; warning
   * them all is how a real signal gets trained out of a team. It never throws: a list that renders
   * with imperfect row identity is worth more than a list that does not render.
   */
  private expansionKey(item: object): unknown {
    if (!this.keyevent && !this.hasWarnedAboutRowIdentity) {
      this.hasWarnedAboutRowIdentity = true;
      console.warn(
        `[${this.tagName.toLowerCase()}] a row was expanded but no .keyevent is set, so row ` +
          'identity falls back to the row object itself. The next fetch replaces those objects, ' +
          'and the open row will close on its own. Supply .keyevent as a function returning the ' +
          "row's key, for example (item) => item.id, or a compound the caller composes, for " +
          'example (item) => item.ownerId + ";" + item.siteId.'
      );
    }
    return this.getPrimaryKey(item);
  }

  getItemRowClass(item: object): string {
    return this.itemRowClassEvent?.(item) ?? '';
  }

  getRowId(item: object): string | null {
    if (this.rowidevent) return this.rowidevent(item);

    return null;
  }

  enableDragOnRow(item: object) {
    if (this.enableDrag !== 'true') return false;

    if (this.checkEnabledDragEvent) return this.checkEnabledDragEvent(item);

    //Default is to return true
    return true;
  }

  /**
   * override this and validate the page is allowed against a max page etc
   * @param value
   * @returns
   */
  protected pageNumberCheck(value: number): number {
    return value;
  }

  protected firstUpdated() {
    if (!this.dataProvider || !this.autoFetchOnLoad || this.hasRequestedData)
      return;
    this.controller.setSliceLength(this.pageLength);
    this.suppressNextProviderReload = true;
    void this.requestDataFromProvider({ reset: true, sliceStart: 0 });
  }

  protected render(): TemplateResult {
    const paginator = this.shouldRenderPaginator()
      ? html`<div class="data-table-paginator-row">
          <data-table-paginator
            @page-change=${(e: CustomEvent) =>
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-member-access
              (this.pageIndex = e.detail.index)}
            index=${this.pageIndex}
            count=${this.pageCount}
          ></data-table-paginator>
          <sl-icon-button
            class="data-table-fullscreen-toggle"
            name=${this.isFullscreen ? 'fullscreen-exit' : 'arrows-fullscreen'}
            label=${
              this.isFullscreen ? 'Exit fullscreen' : 'Expand to fullscreen'
            }
            @click=${this.toggleFullscreen}
          ></sl-icon-button>
        </div>`
      : html``;
    const bodyMaxHeightStyle = this.bodyMaxHeight
      ? styleMap({ maxHeight: this.bodyMaxHeight })
      : styleMap({});
    const minTableWidthStyle = this.minTableWidth
      ? styleMap({ minWidth: this.minTableWidth })
      : styleMap({});

    return html`
      <div
        class="data-table data-table ${this.tablestyle} ${
          this.overflowsSideways ? 'data-table--overflows-x' : ''
        }"
        style=${minTableWidthStyle}
      >
        <div
          class="data-table-scroll"
          style=${bodyMaxHeightStyle}
          @mouseover=${this.handleCellTextHover}
          @mouseleave=${this.collapseCellText}
        >
          <div class="data-table-header data-table-header--sticky">
            <div class="${this.rowClass} data-table-header-row">
              ${this.headerTemplate()}
            </div>
          </div>
          <div class="data-table-content">
            ${until(this.bodyTemplateKept(), this.lastBody)}
          </div>
        </div>
        ${this.loadMoreTemplate()}
        <div class="data-table-footer">${this.footerTemplate()}</div>
        ${paginator}
      </div>
    `;
  }

  /**
   * The body, remembered as the placeholder for the next render (see lastBody). It hands until()
   * bodyTemplate's own promise, so the body lands in the same microtask it always did; the
   * remembering rides on a reaction registered beside it, not on another await in front of it.
   */
  private bodyTemplateKept(): Promise<TemplateResult | TemplateResult[]> {
    const body = this.bodyTemplate();
    void body.then((rendered) => {
      this.lastBody = rendered;
    });
    return body;
  }

  protected async bodyTemplate(): Promise<TemplateResult | TemplateResult[]> {
    const data = this.getPageData();

    // If no groupBy or getGroupByKey, render normally
    if (!this.groupBy && !this.getGroupByKey) {
      return html`${repeat(
        data,
        (item) => this.getPrimaryKey(item),
        (item, index) => this.rowTemplate(item, index)
      )}`;
    }

    // Group rows by the groupBy field or getGroupByKey function
    const groups = this.buildGroups(data);

    // Render with group headers. Headers and rows go through one keyed repeat, as the ungrouped
    // path does (#398 C9): the table keeps its body between renders, so without keys a row
    // inserted above would re-bind every element below it to a different item, and focus or an
    // open dropdown would move to the wrong row.
    const entries: Array<{ key: unknown; render: () => TemplateResult }> = [];
    const runsOfValue = new Map<string, number>();
    let rowIndex = 0;

    for (const group of groups) {
      // A group's value can head more than one run when the rows are not sorted by it, so the
      // header is keyed by its value and which run of that value it is.
      const value = String(group.value);
      const run = runsOfValue.get(value) ?? 0;
      runsOfValue.set(value, run + 1);
      entries.push({
        key: this.groupHeaderKey(`${value}\u0000${run}`),
        render: () => this.renderGroupHeader(group.value, group.items),
      });

      for (const item of group.items) {
        const index = rowIndex++;
        entries.push({
          key: this.getPrimaryKey(item),
          render: () => this.rowTemplate(item, index),
        });
      }
    }

    return html`${repeat(
      entries,
      (entry) => entry.key,
      (entry) => entry.render()
    )}`;
  }

  /**
   * One object per group header, kept for the table's life, used as the header's repeat key. An
   * object rather than the string itself, so no row key (a host's keyevent can return any string)
   * can ever equal a header's key. Grows with the distinct group values the table has shown, which
   * is the size of a lookup's grouping column, not of its rows.
   */
  private groupHeaderKeys = new Map<string, object>();

  private groupHeaderKey(id: string): object {
    let key = this.groupHeaderKeys.get(id);
    if (!key) {
      key = {};
      this.groupHeaderKeys.set(id, key);
    }
    return key;
  }

  private buildGroups(
    data: object[]
  ): Array<{ value: unknown; items: object[] }> {
    const groups: Array<{ value: unknown; items: object[] }> = [];
    let currentGroup: { value: unknown; items: object[] } | null = null;

    for (const item of data) {
      // Use getGroupByKey function if provided, otherwise fall back to field lookup
      const value = this.getGroupByKey
        ? this.getGroupByKey(item)
        : this.getFieldValue(item, this.groupBy!);

      if (currentGroup === null || currentGroup.value !== value) {
        currentGroup = { value, items: [] };
        groups.push(currentGroup);
      }

      currentGroup.items.push(item);
    }

    return groups;
  }

  private renderGroupHeader(
    groupValue: unknown,
    groupItems: object[]
  ): TemplateResult {
    // Custom template provided - wrap it in the standard group header structure
    if (this.groupHeaderTemplate) {
      const customContent = this.groupHeaderTemplate(
        groupValue,
        groupItems,
        this
      );
      return html`
        <div
          class="${this.rowClass} data-table-row data-table-group-header-row"
        >
          <div class="data-table-group-header-cell">${customContent}</div>
        </div>
      `;
    }

    // Default template with simple label and count
    const displayValue =
      groupValue !== null && groupValue !== undefined
        ? String(groupValue)
        : '(No value)';

    return html`
      <div class="${this.rowClass} data-table-row data-table-group-header-row">
        <div class="data-table-group-header-cell">
          <span class="data-table-group-header-label">${displayValue}</span>
          <span class="data-table-group-header-count"
            >(${groupItems.length})</span
          >
        </div>
      </div>
    `;
  }

  private getFieldValue(item: object, fieldName: string): unknown {
    // Handle nested field names like "user.name"
    const parts = fieldName.split('.');
    let value: unknown = item;
    for (const part of parts) {
      if (value === null || value === undefined) return undefined;
      value = (value as Record<string, unknown>)[part];
    }
    return value;
  }

  private loadMoreTemplate(): TemplateResult | typeof nothing {
    if (!this.shouldUseLoadMore) return nothing;
    const loading = this.controllerState?.loading ?? false;
    const hasMore = this.controllerState?.hasMore ?? false;
    if (!hasMore && !loading) return nothing;

    const loadedCount = this.data.length;
    const totalCount = this.controllerState?.totalCount;
    const showCount = hasMore && totalCount !== undefined && totalCount > 0;

    return html`
      <div class="data-table-load-more">
        ${
          showCount
            ? html`<span class="load-more-count"
                >${loadedCount} of ${totalCount} loaded</span
              >`
            : nothing
        }
        <button
          class="load-more-button"
          @click=${this.handleLoadMoreClick}
          ?disabled=${loading || !hasMore}
        >
          ${loading ? 'Loading…' : 'Load more'}
        </button>
        <sl-icon-button
          class="data-table-fullscreen-toggle"
          name=${this.isFullscreen ? 'fullscreen-exit' : 'arrows-fullscreen'}
          label=${
            this.isFullscreen ? 'Exit fullscreen' : 'Expand to fullscreen'
          }
          @click=${this.toggleFullscreen}
        ></sl-icon-button>
      </div>
    `;
  }

  /**
   * THE TABLE'S FULL SCREEN IS THE DOCUMENT'S ONE FULL SCREEN.
   *
   * The architect's rule, 2026-09-19: "Any full screen need to allow normal workflow as though it
   * was not full screen. Any route changes etc should cancel it. Dialogs should be seen. Stacking
   * non allow. One full screen removes a previous". That is a rule about the document, so this
   * claims through `utility/fullscreen-takeover` rather than flipping a flag of its own: a table
   * taking the viewport cancels an expanded `agwa-fullscreen-block` and the other way round, and
   * the table inherits Escape, the document scroll lock and the hidden-tab cancellation from the
   * one place that implements them.
   *
   * NO DEPENDENCE ON THE BLOCK. This is a leaf component and must not import a layout component to
   * be allowed to take the viewport. Both import the registry; neither imports the other.
   */
  private toggleFullscreen() {
    if (this.isFullscreen) this.collapseTakeover();
    else this.enterFullscreen();
  }

  /** Claiming is what cancels whoever held the viewport before this table asked for it. */
  private enterFullscreen() {
    if (this.isFullscreen) return;
    claimTakeover(this);
    this.applyFullscreen(true);
  }

  /**
   * The registry's way out: the table's own control, Escape, and a tab panel that stopped being
   * rendered all arrive here.
   */
  collapseTakeover(): void {
    if (!this.isFullscreen) return;
    releaseTakeover(this);
    this.applyFullscreen(false);
  }

  /**
   * Cancelled by another holder claiming the viewport. It returns to the page exactly as a
   * collapse does but touches NO global state: whoever displaced it owns the document lock and the
   * key listener by now, and releasing them here would free the new holder's lock.
   */
  yieldTakeover(): void {
    this.applyFullscreen(false);
  }

  /**
   * The mode's whole visible effect, in one place so no path can set half of it.
   *
   * The class goes on the HOST rather than inside the shadow root because the rule that positions
   * the table over the viewport is `:host(.data-table-fullscreen)`, which only a class on the host
   * can match. Nothing is written on `<body>`: a `data-table-fullscreen-active` class used to be,
   * read by nothing in the repository, and a table removed while full screened left it there
   * permanently.
   */
  private applyFullscreen(on: boolean) {
    this.isFullscreen = on;
    this.classList.toggle('data-table-fullscreen', on);
  }

  private async handleLoadMoreClick() {
    if (!this.dataProvider || this.controllerState?.loading) return;

    // Save internal scroll position before loading
    const scrollContainer = this.bodyScrollElement;
    const scrollTop = scrollContainer?.scrollTop ?? 0;
    const scrollLeft = scrollContainer?.scrollLeft ?? 0;

    await this.fetchData({ append: true });

    // Restore internal scroll position after render
    if (scrollContainer) {
      requestAnimationFrame(() => {
        scrollContainer.scrollTop = scrollTop;
        scrollContainer.scrollLeft = scrollLeft;
      });
    }
  }

  protected columnTemplate(
    item: any, // eslint-disable-line @typescript-eslint/no-explicit-any
    itemIndex: number,
    colDef: DataTableColumnDef,
    colindex: number,
    columnCount: number
  ): TemplateResult {
    const clickEvent = (colDefClick: DataTableColumnDef) =>
      colDefClick.click
        ? (e: Event) => {
            if (!colDefClick.click?.(e, this, item, colDefClick.fieldName))
              this.cellClickEvent(e, item, colDefClick);
            else e.stopImmediatePropagation();
          }
        : (e: Event) => {
            //dont stop propagation. row click may want this
            this.cellClickEvent(e, item, colDefClick);
          };

    const displayVal = // eslint-disable-line @typescript-eslint/no-unsafe-assignment
      colDef.displayValue?.(this, item, itemIndex, colDef.fieldName) ??
      item[colDef.fieldName] ?? // eslint-disable-line @typescript-eslint/no-unsafe-member-access
      '';

    const posClass =
      colindex === 0 ? 'first' : colindex === columnCount - 1 ? 'last' : '';
    const fixedClass = colDef.fixed
      ? ` data-table-cell--fixed data-table-cell--fixed-${colDef.fixed}`
      : '';
    const alignClass = colDef.align ? `text-${colDef.align}` : '';
    const colClasses =
      `${colDef.classes ?? ''} ${posClass} ${fixedClass} ${alignClass}`.trim();
    const fixedAttr = colDef.fixed ?? nothing;
    const cellStyle = this.getCellWidthStyle(colDef);

    // See DataTableColumnDef.clip for the default's reasoning. The wrapper span is what an
    // ellipsis needs inside a flex cell - a bare text node in a flex container cannot clip.
    const clip = colDef.clip ?? !colDef.displayValue;
    const cellContent = clip
      ? html`<span class="cell-text cell-text--clip">${displayVal}</span>`
      : displayVal;

    return colDef.click
      ? html` <div
          role="button"
          @click=${clickEvent(colDef)}
          class="${this.colClass} ${colClasses}"
          style=${cellStyle}
          data-col-index=${colindex}
          data-fixed=${fixedAttr}
        >
          ${cellContent}
        </div>`
      : html` <div
          @click=${clickEvent(colDef)}
          class="${this.colClass} ${colClasses}"
          style=${cellStyle}
          data-col-index=${colindex}
          data-fixed=${fixedAttr}
        >
          ${cellContent}
        </div>`;
  }

  /** The one clipped span currently expanded under the pointer, if any. */
  private _expandedCellText: HTMLElement | null = null;

  /**
   * Expand-on-hover for clipped cell text (the overflow affordance, architect 2026-09-05):
   * a cropped cell shows an ellipsis, and resting the pointer on it reveals the whole value
   * in place. ONE delegated listener pair on the scroll container rather than per-cell
   * handlers or ResizeObservers - truncation is checked lazily, only for the span actually
   * under the pointer, so row count costs nothing.
   */
  private handleCellTextHover = (e: MouseEvent) => {
    const span =
      e.target instanceof Element
        ? (e.target.closest('.cell-text--clip') as HTMLElement | null)
        : null;
    if (span === this._expandedCellText) return;
    this.collapseCellText();
    if (!span) return;
    // Only content that is actually cropped expands - a value that fits must not grow a
    // bubble that merely repeats it.
    if (span.scrollWidth <= span.clientWidth) return;
    // Cap the overlay at the scroll container's inner edge: it is absolutely positioned
    // inside the cell, so unbounded it would run under the container's clip and the tail
    // would vanish exactly where the ellipsis used to be.
    const scroll = this.renderRoot.querySelector('.data-table-scroll');
    if (scroll) {
      const max =
        scroll.getBoundingClientRect().right -
        span.getBoundingClientRect().left -
        12;
      span.style.maxWidth = `${Math.max(max, 0)}px`;
    }
    span.classList.add('cell-text--expanded');
    // WebKit clamps intrinsic sizing keywords (width: max-content) on absolutely positioned
    // boxes to their containing block - the cell - so on Safari the overlay re-cropped at
    // exactly the column edge it exists to escape (architect, 2026-09-05, live). An explicit
    // pixel width measured from the expanded box's own overflow is honoured by every engine.
    // Engines disagree on whether scrollWidth counts trailing padding and on box-sizing, so
    // one correction pass tops the width up by whatever the first guess left cropped; the
    // max-width cap above still wins at the container's edge.
    span.style.width = `${span.scrollWidth + 2}px`;
    const shortfall = span.scrollWidth - span.clientWidth;
    if (shortfall > 0) {
      span.style.width = `${span.scrollWidth + shortfall + 2}px`;
    }
    // Fixed (sticky) cells are stacking contexts (z-index 3), so an overlay inside one is
    // TRAPPED at its cell's own layer and the next fixed cell - later in DOM order - paints
    // over it: the overlay was full-width in layout and still visually cut at the column
    // edge (architect, 2026-09-05, both engines). Lifting the HOST cell while expanded is
    // the only lever that works from inside a stacking context.
    span.closest('.data-table-cell')?.classList.add('cell-overflow-host');
    this._expandedCellText = span;
  };

  private collapseCellText = () => {
    if (this._expandedCellText) {
      this._expandedCellText.classList.remove('cell-text--expanded');
      this._expandedCellText.style.maxWidth = '';
      this._expandedCellText.style.width = '';
      this._expandedCellText
        .closest('.data-table-cell')
        ?.classList.remove('cell-overflow-host');
      this._expandedCellText = null;
    }
  };

  private getCellWidthStyle(colDef: DataTableColumnDef) {
    const styles: Record<string, string> = {};

    // Use explicit width if provided
    if (colDef.width) {
      styles.flex = `0 0 ${colDef.width}`;
      styles.width = colDef.width;
    } else if (this.autoSizeColumns) {
      // Use computed width if auto-sizing is enabled and width was calculated
      const computedWidth = this._computedWidths.get(colDef.fieldName);
      if (computedWidth) {
        styles.flex = `0 0 ${computedWidth}px`;
        styles.width = `${computedWidth}px`;
      }
      // If no computed width yet, use auto to allow measurement
    }

    if (colDef.minWidth) {
      styles.minWidth = colDef.minWidth;
    }
    if (colDef.maxWidth) {
      styles.maxWidth = colDef.maxWidth;
    }
    return styleMap(styles);
  }

  private isVisible(column: DataTableColumnDef): boolean {
    if (column.visible !== undefined) {
      return column.visible;
    }

    // Default to true if visible is not set
    return true;
  }

  private getVisibleColumnGroups(): {
    ordered: Array<{ column: DataTableColumnDef; index: number }>;
    left: Array<{ column: DataTableColumnDef; index: number }>;
    center: Array<{ column: DataTableColumnDef; index: number }>;
    right: Array<{ column: DataTableColumnDef; index: number }>;
    total: number;
  } {
    const ordered: Array<{ column: DataTableColumnDef; index: number }> = [];
    const left: typeof ordered = [];
    const center: typeof ordered = [];
    const right: typeof ordered = [];
    let visibleIndex = 0;
    this.columns.forEach((column) => {
      if (!this.isVisible(column)) return;
      const entry = { column, index: visibleIndex };
      ordered.push(entry);
      if (column.fixed === 'left') left.push(entry);
      else if (column.fixed === 'right') right.push(entry);
      else center.push(entry);
      visibleIndex++;
    });
    return { ordered, left, center, right, total: visibleIndex };
  }

  public rowTemplate(item: object, index: number): TemplateResult {
    const groups = this.getVisibleColumnGroups();
    const columnCount = groups.total;

    const renderSegmentCells = (
      entries: Array<{ column: DataTableColumnDef; index: number }>
    ) =>
      entries.map(({ column, index: columnIndex }) =>
        this.columnTemplate(item, index, column, columnIndex, columnCount)
      );

    const leftCells = renderSegmentCells(groups.left);
    const centerCells = renderSegmentCells(groups.center);
    const rightCells = renderSegmentCells(groups.right);

    const itemRowClasses = this.getItemRowClass(item);
    const extensionPromise = this.createExtensionTemplate(item);
    const extensionKey = this.getPrimaryKey(item);
    // While the panel's content is built, the content it last showed stays (lastExtensionContent);
    // "Loading" only for a panel opening for the first time.
    const extensionTemplateWrapper = extensionPromise
      ? html`${until(
          extensionPromise.then((result) => {
            const content = html`
              <div class="data-table-row-extension">${result.template}</div>
            `;
            this.lastExtensionContent.set(extensionKey, content);
            return content;
          }),
          this.lastExtensionContent.get(extensionKey) ??
            html`<div class="data-table-row-extension">
              <h1>Loading....</h1>
            </div>`
        )}`
      : undefined;

    const rowClick = (e: Event) => {
      e.stopImmediatePropagation();
      this.rowClicked(item);
    };
    const rowKeyDown = (event: KeyboardEvent) => {
      this.handleRowKeyDown(event, item);
    };
    // check if current item key is in array, if so, add custom class to highlight row
    const itemKey = this.getPrimaryKey(item);
    const isSelected = this.selectedKeys.indexOf(itemKey) > -1;
    const enableDragDrop = this.enableDragOnRow(item);
    const noDrop: string =
      this.enableDrag === 'true' && !enableDragDrop ? this.noDropClass : '';

    const clickable = this.clickrows || this.isSelectable;
    const beforeRowTemplate = this.beforeRowTemplate(item, index);
    const afterRowTemplate = this.afterRowTemplate(item, index);

    return html`${beforeRowTemplate}
      <div
        draggable=${enableDragDrop ? 'true' : 'false'}
        @click=${clickable ? rowClick : undefined}
        @keydown=${clickable ? rowKeyDown : undefined}
        role=${ifDefined(clickable ? 'button' : undefined)}
        tabindex=${ifDefined(clickable ? 0 : undefined)}
        aria-selected=${ifDefined(
          this.isSelectable ? (isSelected ? 'true' : 'false') : undefined
        )}
        class="${this.rowClass} ${itemRowClasses} data-table-row ${
          isSelected ? 'selected' : ''
        } ${noDrop}"
        @dragstart="${this.handleDragStart}"
        @dragover="${this.handleDragOver}"
        @dragleave="${this.handleDragLeave}"
        @drop="${this.handleDrop}"
        @dragend="${this.handleDragEnd}"
        data-index="${index}"
        data-rowid=${this.getRowId(item) ?? nothing}
      >
        ${
          groups.left.length
            ? html`<div
                class="data-table-row-segment data-table-row-segment-left"
              >
                ${leftCells}
              </div>`
            : nothing
        }
        ${
          groups.center.length
            ? html`<div
                class="data-table-row-segment data-table-row-segment-center"
              >
                <div class="data-table-row-center-track">${centerCells}</div>
              </div>`
            : nothing
        }
        ${
          groups.right.length
            ? html`<div
                class="data-table-row-segment data-table-row-segment-right"
              >
                ${rightCells}
              </div>`
            : nothing
        }
      </div>
      ${extensionTemplateWrapper ?? ''}${this.childRowsTemplate(
        item,
        index
      )}${afterRowTemplate}`;
  }

  /**
   * The expanded item's child rows, rendered through the SAME columns as their parent.
   *
   * Ordered after the extension panel so that a row using both reads parent, then its panel, then
   * its children, which is the order they belong to each other in.
   */
  private childRowsTemplate(item: object, parentIndex: number) {
    if (!this.isChildRowsExpanded(item)) return nothing;
    const children = this.childRowsFor(item);
    if (children.length === 0) return nothing;
    return html`${children.map((child, childIndex) =>
      this.childRowTemplate(child, parentIndex, childIndex)
    )}`;
  }

  /**
   * One child row.
   *
   * Built from the same `getVisibleColumnGroups()` and the same `columnTemplate` as a parent row, so
   * the two cannot fall out of alignment: there is no second set of widths to keep in step, because
   * there is no second set of widths.
   *
   * It carries NEITHER `rowClass` NOR `data-table-row`, and that is deliberate rather than an
   * omission. `rowClass` marks the rows the table navigates, drags and cleans up after
   * (`.${this.rowClass}` is queried for all three), and a child row is none of those things - it is
   * presentation belonging to its parent. Carrying `data-table-row` would not help either, because
   * `rowClass` DEFAULTS to exactly that string, so the two are the same selector on any table that
   * has not overridden it, and child rows would be enrolled in drag and keyboard navigation that no
   * consumer asked for. A unit test asserts this and caught precisely that mistake.
   *
   * The grid layout is shared instead by naming both classes in the stylesheet's layout rule, so
   * there is one set of layout declarations serving both and no copy to drift.
   */
  private childRowTemplate(
    child: object,
    parentIndex: number,
    childIndex: number
  ): TemplateResult {
    const groups = this.getVisibleColumnGroups();
    const columnCount = groups.total;
    const renderSegmentCells = (
      entries: Array<{ column: DataTableColumnDef; index: number }>
    ) =>
      entries.map(({ column, index: columnIndex }) =>
        this.columnTemplate(
          child,
          parentIndex,
          column,
          columnIndex,
          columnCount
        )
      );

    // A child row opens its own thing when the table is clickable.
    //
    // Wired HERE rather than by giving the child `rowClass`, which is what the note above forbids:
    // `rowClass` is the selector for drag, keyboard navigation and cleanup, and a child row must
    // stay out of all three. Click is the one behaviour it SHOULD share, so it is attached directly
    // and emits the same `rowclick` event with the child as its item - the consumer decides what a
    // child means. Without this a child row was inert, which read as a broken row rather than as a
    // deliberate distinction.
    const clickable = this.clickrows || this.isSelectable;
    return html`<div
      class="data-table-child-row"
      draggable="false"
      role=${clickable ? 'button' : nothing}
      tabindex=${clickable ? 0 : nothing}
      @click=${clickable ? () => this.rowClicked(child) : nothing}
      @keydown=${
        clickable
          ? (e: KeyboardEvent) => this.handleRowKeyDown(e, child)
          : nothing
      }
      data-parent-index="${parentIndex}"
      data-child-index="${childIndex}"
    >
      ${
        groups.left.length
          ? html`<div
              class="data-table-row-segment data-table-row-segment-left"
            >
              ${renderSegmentCells(groups.left)}
            </div>`
          : nothing
      }
      ${
        groups.center.length
          ? html`<div
              class="data-table-row-segment data-table-row-segment-center"
            >
              <div class="data-table-row-center-track">
                ${renderSegmentCells(groups.center)}
              </div>
            </div>`
          : nothing
      }
      ${
        groups.right.length
          ? html`<div
              class="data-table-row-segment data-table-row-segment-right"
            >
              ${renderSegmentCells(groups.right)}
            </div>`
          : nothing
      }
    </div>`;
  }

  private handleRowKeyDown(event: KeyboardEvent, item: object) {
    if (!this.clickrows && !this.isSelectable) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.rowClicked(item);
  }

  private handleTouchCancel(_e: TouchEvent) {
    this.cleanupDragEnd();
  }

  private dragtouchstart(event: TouchEvent) {
    const row: HTMLDivElement = (event.target as HTMLElement).closest(
      `.${this.rowClass}`
    )!;

    if (!row.draggable) return;

    row.classList.add('ghost-row');
    this.dragSrcEl = row;
    this.draggedIndex = Number(row.dataset.index);
  }

  private dragtouchmove(event: TouchEvent) {
    event.preventDefault();
    const touch = event.touches[0];

    const targetRow: HTMLDivElement | null | undefined = document
      .elementFromPoint(touch.clientX, touch.clientY)
      ?.closest(`.${this.rowClass}[draggable="true"]`);
    if (!targetRow) {
      this.lastDropTarget?.classList.remove('drop-target-at');
      this.lastDropTarget = null;
      return;
    }

    if (targetRow !== this.dragSrcEl && targetRow !== this.lastDropTarget) {
      this.lastDropTarget?.classList.remove('drop-target-at');

      targetRow.classList.add('drop-target-at');
      this.lastDropTarget = targetRow;
    }
  }

  private dragtouchend(_event: TouchEvent) {
    if (this.lastDropTarget) this.dropHandle(this.lastDropTarget);

    this.cleanupDragEnd();
  }

  private handleDragStart(e: DragEvent): void {
    const target = e.currentTarget as HTMLDivElement;

    if (this.enableDrag !== 'true') {
      e.stopPropagation();
      e.preventDefault();
      return;
    }

    this.dragSrcEl = target;
    this.draggedIndex = Number(target.dataset.index);
    target.classList.add('ghost-row');
    e.dataTransfer!.effectAllowed = 'move';
    e.dataTransfer!.setDragImage(target, 0, 0);
  }

  private handleDragOver(e: DragEvent): void {
    e.preventDefault();
    e.dataTransfer!.dropEffect = 'move';
    const target = e.currentTarget as HTMLDivElement;

    if (this.enableDrag !== 'true') {
      e.stopPropagation();
      return;
    }

    if (target !== this.dragSrcEl) {
      target.classList.add('drop-target-at');
    }
  }

  private handleDragLeave(e: DragEvent): void {
    const target = e.currentTarget as HTMLElement;
    target.classList.remove('drop-target-at');
  }

  private handleDrop(e: DragEvent) {
    e.preventDefault();
    const target = e.currentTarget as HTMLElement;
    this.dropHandle(target);
  }

  private dropHandle(target: HTMLElement) {
    if (this.enableDrag !== 'true') {
      return;
    }

    if (
      target !== this.dragSrcEl &&
      this.draggedIndex !== null &&
      !target.classList.contains(this.noDropClass)
    ) {
      const insertAt = Number(target.dataset.index);
      const insertObject: TableItemMove = {
        sourceIndex: this.draggedIndex,
        targetIndex: insertAt,
      };
      this.dispatchCustom('-table-item-move', insertObject);
    }
  }

  private handleDragEnd(e: DragEvent): void {
    e.preventDefault();
    this.cleanupDragEnd();
  }

  private cleanupDragEnd() {
    // Through renderRoot: the rows are in this component's SHADOW root, and `this.querySelectorAll`
    // searches the host's light children, which are empty. It matched nothing, so a finished drag
    // left the ghost class on the row it moved and the drop-target class on every row it crossed -
    // the table stayed visibly mid-drag with nothing being dragged.
    const rows = this.renderRoot.querySelectorAll(`.${this.rowClass}`);
    rows.forEach((row) => {
      row.classList.remove('ghost-row');
      row.classList.remove('drop-target-at');
    });
    // Ensure cleanup on drag end
    this.dragSrcEl = null;
    this.draggedIndex = null;
  }

  protected createExtensionTemplate(
    item: object
  ): Promise<DataTableExtensionResult> | null {
    const itemKey = this.getPrimaryKey(item);
    const extensionProvider = this._extensions.find(
      (x) => x.itemKey === itemKey
    )?.extensionProvider;
    if (!extensionProvider) return null;

    const normalizeResult = (
      result: TemplateResult | DataTableExtensionResult
    ): DataTableExtensionResult => {
      // Check if it's already a compound result with template property
      if (result && typeof result === 'object' && 'template' in result) {
        return result;
      }
      // It's a plain TemplateResult, wrap it
      return { template: result };
    };

    if (typeof extensionProvider === 'function') {
      const func = extensionProvider;
      const result = func(item);
      if (result instanceof Promise) {
        return result.then(normalizeResult);
      }
      return Promise.resolve(normalizeResult(result));
    } else {
      if (extensionProvider instanceof Promise) {
        return extensionProvider.then(normalizeResult);
      }
      return Promise.resolve(normalizeResult(extensionProvider));
    }
  }

  /**
   * Adopt the region that scrolls the rows, and the header that has to answer to it.
   *
   * Through renderRoot, because both live in this component's SHADOW root. Queried against the
   * host they were always null, so this took the teardown branch on EVERY render: no scroll
   * listener was ever attached, the header never got its scrollbar-gutter compensation, and the
   * component never learned it had a scroll region at all - which is also what left
   * `bodyScrollElement` null for the scroll-position restores around load-more and row expansion.
   */
  private setupScrollContainers() {
    const newBody =
      this.renderRoot.querySelector<HTMLElement>('.data-table-scroll');
    const newHeader =
      this.renderRoot.querySelector<HTMLElement>('.data-table-header');

    if (!newBody || !newHeader) {
      this.teardownScrollContainers();
      return;
    }

    if (this.bodyScrollElement !== newBody) {
      this.attachBodyScrollElement(newBody);
    }

    // The header takes the rows' content width (min-width: max-content), so its box grows when
    // the columns do without a render (a late web font), which the scroller's own box never
    // shows. Watched so the sideways-overflow measurement follows it.
    if (this.headerContainerElement !== newHeader && this.resizeObserver) {
      if (this.headerContainerElement)
        this.resizeObserver.unobserve(this.headerContainerElement);
      this.resizeObserver.observe(newHeader);
    }
    this.headerContainerElement = newHeader;
    this.syncHeaderScrollPosition();
  }

  private attachBodyScrollElement(element: HTMLElement) {
    this.detachBodyScrollElement();
    this.bodyScrollElement = element;
    this.bodyScrollListener = () => this.syncHeaderScrollPosition();
    element.addEventListener('scroll', this.bodyScrollListener, {
      passive: true,
    });
    if (this.resizeObserver) this.resizeObserver.observe(element);
  }

  private detachBodyScrollElement() {
    if (this.bodyScrollElement && this.bodyScrollListener) {
      this.bodyScrollElement.removeEventListener(
        'scroll',
        this.bodyScrollListener
      );
    }
    if (this.resizeObserver && this.bodyScrollElement) {
      this.resizeObserver.unobserve(this.bodyScrollElement);
    }
    this.bodyScrollElement = null;
    this.bodyScrollListener = undefined;
  }

  private teardownScrollContainers() {
    this.detachBodyScrollElement();
    if (this.headerContainerElement) {
      const headerRow = this.headerContainerElement.querySelector<HTMLElement>(
        '.data-table-header-row'
      );
      headerRow?.style.removeProperty('transform');
      headerRow?.style.removeProperty('width');
      this.headerContainerElement.style.removeProperty('padding-right');
    }
    this.headerContainerElement = null;
  }

  private syncHeaderScrollPosition() {
    // Header is inside scroll container, so horizontal scroll works natively.
    // We only need to handle the scrollbar gutter compensation for header
    // and sync the control scroller position.

    if (this.headerContainerElement && this.bodyScrollElement) {
      const gutter =
        this.bodyScrollElement.offsetWidth - this.bodyScrollElement.clientWidth;
      if (gutter > 0)
        this.headerContainerElement.style.paddingRight = `${gutter}px`;
      else this.headerContainerElement.style.removeProperty('padding-right');
    }
  }

  private observeSizeChanges() {
    if (typeof ResizeObserver === 'undefined') return;
    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.queueFixedColumnOffsets();
        this.syncHeaderScrollPosition();
        this.measureSidewaysOverflow();
      });
    }
    this.resizeObserver.observe(this);
    if (this.bodyScrollElement)
      this.resizeObserver.observe(this.bodyScrollElement);
    if (this.headerContainerElement)
      this.resizeObserver.observe(this.headerContainerElement);
  }

  /**
   * Measures header and body cells to compute optimal column widths.
   * Uses an offscreen measurement container to get accurate natural widths.
   * Only affects columns without explicit width property.
   */
  private computeAutoColumnWidths() {
    if (!this.autoSizeColumns) {
      return;
    }

    // Get columns that need auto-sizing (no explicit width)
    const columnsToMeasure = this.columns.filter(
      (col) => !col.width && this.isVisible(col)
    );
    if (columnsToMeasure.length === 0) return;

    // Use renderRoot (shadowRoot) to query elements in Shadow DOM
    const root = this.renderRoot;
    const headerRow = root.querySelector<HTMLElement>('.data-table-header-row');
    const contentArea = root.querySelector<HTMLElement>('.data-table-content');
    if (!headerRow) return;

    // Create an offscreen measurement container
    const measureContainer = document.createElement('div');
    measureContainer.style.cssText = `
      position: absolute;
      visibility: hidden;
      height: auto;
      width: auto;
      white-space: nowrap;
      font-family: var(--data-table-font, 'Space Grotesk', 'Segoe UI', system-ui, sans-serif);
      font-size: 0.875rem;
      padding: 0.35rem 0.75rem;
    `;
    document.body.appendChild(measureContainer);

    // Separate container for header (slightly different styling)
    const headerMeasureContainer = document.createElement('div');
    headerMeasureContainer.style.cssText = `
      position: absolute;
      visibility: hidden;
      height: auto;
      width: auto;
      white-space: nowrap;
      font-family: var(--data-table-font, 'Space Grotesk', 'Segoe UI', system-ui, sans-serif);
      font-size: 0.78rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.02em;
      padding: 0.35rem 0.75rem;
    `;
    document.body.appendChild(headerMeasureContainer);

    const newWidths = new Map<string, number>();
    const MIN_WIDTH = 60; // Minimum column width in pixels
    const EXTRA_PADDING = 16; // Extra padding for sort icons, borders, etc.

    for (const colDef of columnsToMeasure) {
      let maxWidth = MIN_WIDTH;
      const colIndex = this.columns.findIndex(
        (c) => c.fieldName === colDef.fieldName && this.isVisible(c)
      );
      // Measure header cell content
      const headerCell = headerRow.querySelector<HTMLElement>(
        `.data-table-cell[data-col-index="${colIndex}"]`
      );
      if (headerCell) {
        // Clone the content to measure
        headerMeasureContainer.innerHTML = headerCell.innerHTML;
        const headerWidth = headerMeasureContainer.offsetWidth + EXTRA_PADDING;
        maxWidth = Math.max(maxWidth, headerWidth);
      }

      // Measure body cells (sample first 20 rows for performance)
      if (contentArea) {
        const bodyCells = contentArea.querySelectorAll<HTMLElement>(
          `.data-table-cell[data-col-index="${colIndex}"]`
        );
        let rowCount = 0;
        for (const cell of bodyCells) {
          if (rowCount >= 20) break;
          measureContainer.innerHTML = cell.innerHTML;
          const cellWidth = measureContainer.offsetWidth + EXTRA_PADDING;
          maxWidth = Math.max(maxWidth, cellWidth);
          rowCount++;
        }
      }

      // Apply max-width constraint if specified
      if (colDef.maxWidth) {
        const maxWidthPx = parseInt(colDef.maxWidth, 10);
        if (!isNaN(maxWidthPx)) {
          maxWidth = Math.min(maxWidth, maxWidthPx);
        }
      }

      // Apply min-width constraint if specified
      if (colDef.minWidth) {
        const minWidthPx = parseInt(colDef.minWidth, 10);
        if (!isNaN(minWidthPx)) {
          maxWidth = Math.max(maxWidth, minWidthPx);
        }
      }

      newWidths.set(colDef.fieldName, Math.ceil(maxWidth));
    }

    // Clean up measurement containers
    document.body.removeChild(measureContainer);
    document.body.removeChild(headerMeasureContainer);

    // Only update if widths have changed
    let hasChanges = false;
    for (const [field, width] of newWidths) {
      if (this._computedWidths.get(field) !== width) {
        hasChanges = true;
        break;
      }
    }

    if (hasChanges) {
      this._computedWidths = newWidths;
      // Force re-render to apply the new widths
      this.requestUpdate();
    } else {
      //
    }
  }

  private scheduleAutoSize() {
    if (!this.autoSizeColumns || this._autoSizeScheduled) return;
    this._autoSizeScheduled = true;

    // Wait for render to complete, then measure
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this._autoSizeScheduled = false;
        this.computeAutoColumnWidths();
      });
    });
  }

  private queueFixedColumnOffsets() {
    if (
      typeof window === 'undefined' ||
      typeof requestAnimationFrame === 'undefined'
    ) {
      this.applyFixedColumnOffsets();
      return;
    }
    if (this.fixedColumnOffsetFrame !== null) return;
    this.fixedColumnOffsetFrame = requestAnimationFrame(() => {
      this.fixedColumnOffsetFrame = null;
      this.applyFixedColumnOffsets();
      this.measureSidewaysOverflow();
    });
  }

  /** Measure sideways overflow once, in the next frame, after the render that asked for it. */
  private queueOverflowMeasure() {
    if (
      typeof window === 'undefined' ||
      typeof requestAnimationFrame === 'undefined'
    ) {
      this.measureSidewaysOverflow();
      return;
    }
    if (this.overflowMeasureFrame !== null) return;
    this.overflowMeasureFrame = requestAnimationFrame(() => {
      this.overflowMeasureFrame = null;
      this.measureSidewaysOverflow();
    });
  }

  /**
   * Record whether the table is wider than its scroller (see `overflowsSideways`). Runs with the
   * fixed-column offsets, on every size change and every data or column change. A scroller with
   * no layout (a list in a tab not yet shown) has nothing to measure, so it keeps its last answer
   * rather than reading zero widths as "fits".
   */
  private measureSidewaysOverflow() {
    const scroller = this.bodyScrollElement;
    if (!scroller || scroller.clientWidth === 0) return;
    // One pixel of slack: sub-pixel column widths can round the scroll width up by one.
    const overflows = scroller.scrollWidth > scroller.clientWidth + 1;
    if (overflows === this.overflowsSideways) return;
    this.overflowsSideways = overflows;
    // The right-hand offsets follow the pin, so rewrite them for the new state.
    this.queueFixedColumnOffsets();
  }

  private applyFixedColumnOffsets() {
    const hasFixedColumns = this.columns.some(
      (column) => column.fixed && this.isVisible(column)
    );
    if (!hasFixedColumns) {
      this.clearFixedColumnOffsets();
      return;
    }

    // Through renderRoot: the header row is in this component's SHADOW root. Queried against the
    // host it was always null, so this returned here every time and no pinned column was ever
    // given an offset - `.data-table-cell--fixed` is `position: sticky` with no `left`/`right`,
    // which pins nothing at all.
    const headerRow = this.renderRoot.querySelector<HTMLElement>(
      '.data-table-header-row'
    );
    if (!headerRow) return;

    const headerCells = Array.from(
      headerRow.querySelectorAll<HTMLElement>(
        '.data-table-cell[data-col-index]'
      )
    );
    if (!headerCells.length) return;

    const leftOffsets = new Map<number, number>();
    let cumulativeLeft = 0;
    headerCells
      .filter((cell) => cell.dataset.fixed === 'left')
      .sort(
        (a, b) =>
          Number(a.dataset.colIndex ?? 0) - Number(b.dataset.colIndex ?? 0)
      )
      .forEach((cell) => {
        const columnIndex = Number(cell.dataset.colIndex ?? 0);
        leftOffsets.set(columnIndex, cumulativeLeft);
        cumulativeLeft += cell.getBoundingClientRect().width;
      });

    const rightOffsets = new Map<number, number>();
    let cumulativeRight = 0;
    headerCells
      .filter((cell) => cell.dataset.fixed === 'right')
      .sort(
        (a, b) =>
          Number(b.dataset.colIndex ?? 0) - Number(a.dataset.colIndex ?? 0)
      )
      .forEach((cell) => {
        const columnIndex = Number(cell.dataset.colIndex ?? 0);
        rightOffsets.set(columnIndex, cumulativeRight);
        cumulativeRight += cell.getBoundingClientRect().width;
      });

    // A TABLE WITH NO LAYOUT HAS NOTHING TO MEASURE, so keep the offsets it already has rather
    // than replacing them with a sum of zeros. Every panel of a tabbed page is in the DOM from the
    // first render and only the active one is displayed, so a list in a tab computes this while it
    // has no boxes at all: measured on the company workspace's Staff tab, its three pinned columns
    // all came out at `left: 0px` - every one of them at the same place - and only became the real
    // 0 / 70.19 / 291.38 once the window was resized with the tab open. Writing that was harmless
    // only because the sticky segments do the pinning; it was still a wrong number in the DOM.
    if (cumulativeLeft + cumulativeRight === 0) return;

    this.applyOffsetsForSide(leftOffsets, 'left');
    // A right column pins only while the table overflows sideways (see `overflowsSideways`);
    // otherwise it takes no offset, so a second right-pinned column is not shifted inward.
    this.applyOffsetsForSide(
      this.overflowsSideways ? rightOffsets : new Map<number, number>(),
      'right'
    );
  }

  /**
   * Write one side's offsets onto the cells that are pinned to it, and take the offset back off
   * any column that used to be.
   *
   * THE SECOND HALF IS WHY THE REMEMBERED SET EXISTS. A column that stops being pinned keeps its
   * cell: Lit reuses the same `<div>` and merely drops the `data-fixed` attribute, while the
   * inline `left` written here stays behind. `.data-table-cell` is `position: relative` (the
   * overflow affordance anchors on it), so a stranded `left: 120px` shifts an ordinary cell
   * sideways by 120px. A query on `[data-fixed]` cannot reach those cells, because the attribute
   * saying they were pinned is exactly what has gone; the indices this wrote last time are the
   * only record of them. The lists a consumer builds do change shape - the reports and products
   * lists drop their register column on a host-scoped page - so this is a real path, not a
   * defensive one.
   *
   * It was unreachable until now only because the queries below searched the light DOM and found
   * nothing to write in the first place.
   */
  private applyOffsetsForSide(
    offsets: Map<number, number>,
    side: 'left' | 'right'
  ) {
    for (const columnIndex of this.offsetColumnsWritten[side]) {
      if (offsets.has(columnIndex)) continue;
      this.renderRoot
        .querySelectorAll<HTMLElement>(
          `.data-table-cell[data-col-index="${columnIndex}"]`
        )
        .forEach((cell) => cell.style.removeProperty(side));
    }
    this.offsetColumnsWritten[side] = new Set(offsets.keys());

    const cells = this.renderRoot.querySelectorAll<HTMLElement>(
      `.data-table-cell[data-fixed="${side}"]`
    );
    cells.forEach((cell) => {
      const columnIndex = Number(cell.dataset.colIndex ?? '-1');
      if (!offsets.has(columnIndex)) {
        cell.style.removeProperty(side);
        return;
      }
      cell.style.setProperty(side, `${offsets.get(columnIndex)}px`);
    });
  }

  /**
   * Take every pinning offset back off, for a table that has stopped pinning anything.
   *
   * Addressed by the column indices this last wrote rather than by `[data-fixed]`, for the reason
   * `applyOffsetsForSide` gives: by the time this runs the attribute is already gone. It also
   * means a table that never pinned anything - most of them - does no work here at all, which
   * matters because this is reached from the resize observer on every frame of a window drag.
   */
  private clearFixedColumnOffsets() {
    for (const side of ['left', 'right'] as const) {
      for (const columnIndex of this.offsetColumnsWritten[side]) {
        this.renderRoot
          .querySelectorAll<HTMLElement>(
            `.data-table-cell[data-col-index="${columnIndex}"]`
          )
          .forEach((cell) => cell.style.removeProperty(side));
      }
      this.offsetColumnsWritten[side] = new Set();
    }
  }

  protected rowClicked(item: object) {
    if (this.isSelectable) {
      const itemKey = this.getPrimaryKey(item);
      if (this.selectmode === 'single') {
        this.selectedKeys = [itemKey];
      } else if (this.selectmode === 'multi') {
        const keyIndex = this.selectedKeys.indexOf(itemKey);
        if (keyIndex === -1) {
          this.selectedKeys = [
            ...this.selectedKeys.filter((x) => x !== itemKey),
            itemKey,
          ];
        } else {
          this.selectedKeys = this.selectedKeys.filter((x) => x !== itemKey);
        }
      }
      this.rowSelectEvent();
    }
    if (this.clickrows) {
      this.rowClickEvent(item);
    }
  }

  protected getPageData(): object[] {
    //can be converted to return a range for the page;
    return this.data;
  }

  protected calculateFirstIndex(
    _data: unknown[],
    pageIndex: number,
    pageLength: number
  ) {
    return pageIndex * pageLength;
  }

  protected getColumnHeaderTemplate(
    defs: DataTableColumnDef,
    index: number,
    totalColumns: number
  ) {
    const columnSortEvent = (e: Event) => {
      e.stopImmediatePropagation();
      if (this.sortField === defs.fieldName) {
        this.sortAscending = !this.sortAscending;
      } else {
        this.sortField = defs.fieldName;
        this.sortAscending = defs.isDefaultSortDirectionAscending ?? true;
      }
      this.pageIndex = 0;
    };

    const cellStyle = this.getCellWidthStyle(defs);
    const alignClass = defs.align ? `text-${defs.align}` : '';

    if (defs.sortable) {
      const isAscendingSort =
        this.isSortingAscending ?? defs.isDefaultSortDirectionAscending;
      const isColumnBeingSorted = this.sortField === defs.fieldName;
      const sortDirectionClass = isColumnBeingSorted
        ? isAscendingSort
          ? 'sorting_asc'
          : 'sorting_desc'
        : '';
      const posClass =
        index === 0 ? 'first' : index === totalColumns - 1 ? 'last' : '';
      const fixedClass = defs.fixed
        ? ` data-table-cell--fixed data-table-cell--fixed-${defs.fixed}`
        : '';
      const fixedAttr = defs.fixed ?? nothing;
      const classes =
        `${this.colClass} ${defs.classes ?? ''} sorting ${sortDirectionClass} ${posClass} ${fixedClass} ${alignClass}`.trim();
      return html` <div
        role="button"
        @click=${columnSortEvent}
        class="${classes}"
        style=${cellStyle}
        data-col-index=${index}
        data-fixed=${fixedAttr}
      >
        ${awaitableTemplate(defs.title)}
      </div>`;
    }
    const posClass =
      index === 0 ? 'first' : index === totalColumns - 1 ? 'last' : '';
    const fixedClass = defs.fixed
      ? ` data-table-cell--fixed data-table-cell--fixed-${defs.fixed}`
      : '';
    const fixedAttr = defs.fixed ?? nothing;
    return html` <div
      class="${this.colClass} ${
        defs.classes ?? ''
      } sorting-disabled ${posClass} ${fixedClass} ${alignClass}"
      style=${cellStyle}
      data-col-index=${index}
      data-fixed=${fixedAttr}
    >
      ${awaitableTemplate(defs.title)}
    </div>`;
  }

  protected headerTemplate(): unknown {
    const groups = this.getVisibleColumnGroups();
    const totalColumns = groups.total;
    const renderSegment = (
      entries: Array<{ column: DataTableColumnDef; index: number }>,
      segment: 'left' | 'center' | 'right'
    ) => {
      if (!entries.length) return nothing;
      const content = entries.map(({ column, index }) =>
        this.getColumnHeaderTemplate(column, index, totalColumns)
      );
      if (segment === 'center') {
        return html`<div
          class="data-table-header-segment data-table-header-segment-center"
        >
          <div class="data-table-header-center-track">${content}</div>
        </div>`;
      }
      const segmentClass =
        segment === 'left'
          ? 'data-table-header-segment-left'
          : 'data-table-header-segment-right';
      return html`<div class="data-table-header-segment ${segmentClass}">
        ${content}
      </div>`;
    };

    return html`
      ${renderSegment(groups.left, 'left')}
      ${renderSegment(groups.center, 'center')}
      ${renderSegment(groups.right, 'right')}
    `;
  }

  protected footerTemplate(): unknown {
    if (
      !this.columns.some(
        (x) => typeof x.columnAggregate === 'function' && this.isVisible(x)
      )
    )
      return html``;

    const getTotalColTemplate = (
      column: DataTableColumnDef,
      index: number,
      totalColumns: number
    ) => {
      const value =
        typeof column.columnAggregate === 'function'
          ? column.columnAggregate()
          : html``;
      const posClass =
        index === 0 ? 'first' : index === totalColumns - 1 ? 'last' : '';
      const fixedClass = column.fixed
        ? ` data-table-cell--fixed data-table-cell--fixed-${column.fixed}`
        : '';
      const fixedAttr = column.fixed ?? nothing;
      const cellStyle = this.getCellWidthStyle(column);

      return html` <div
        class="${this.colClass} ${
          column.classes ?? ''
        } ${posClass} ${fixedClass}"
        style=${cellStyle}
        data-col-index=${index}
        data-fixed=${fixedAttr}
      >
        ${value}
      </div>`;
    };

    const groups = this.getVisibleColumnGroups();
    const totalColumns = groups.total;
    const orderedColumns = [...groups.ordered].sort(
      (a, b) => a.index - b.index
    );
    const cols = orderedColumns.map(({ column, index }) =>
      getTotalColTemplate(column, index, totalColumns)
    );

    return html` <div class="${this.rowClass} data-table-row">${cols}</div>`;
  }

  private dispatchCustom(name: string, detail: object) {
    const newEvent = new CustomEvent(name, {
      detail: detail,
      bubbles: false,
      composed: true,
    });
    this.dispatchEvent(newEvent);
  }
}

const DATA_TABLE_TAG = 'agwa-lit-table';

if (!customElements.get(DATA_TABLE_TAG)) {
  customElements.define(DATA_TABLE_TAG, DataTable);
}

declare global {
  interface HTMLElementTagNameMap {
    'data-table-paginator': DataTablePaginator;

    'agwa-lit-table': DataTable;
  }
}
